import { Fragment, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n';
import { LoopVideo } from '../LoopVideo';

/**
 * Ogni lettera e' un inline-block per poter essere animata, e un inline-block e'
 * un punto in cui il browser puo' andare a capo: senza raggrupparle per parola
 * l'h1 si spezzava a meta' ("di em / ozioni") appena la riga si stringeva.
 * Il gruppo-parola e' anch'esso inline-block, che al suo interno non si rompe.
 */
const Lettere = ({ text, className = '', delay = 0 }) => {
  let n = -1;
  const parole = text.split(' ');
  return (
    <span className={className} aria-hidden>
      {parole.map((parola, wi) => (
        <Fragment key={`${parola}-${wi}`}>
        <span style={{ display: 'inline-block' }}>
          {parola.split('').map((ch, ci) => {
            n += 1;
            return (
              <motion.span
                key={`${ch}-${ci}`}
                initial={{ opacity: 0, y: '0.5em', rotateX: -55 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 0.85, delay: delay + n * 0.035, ease: [0.22, 1, 0.36, 1] }}
                style={{ display: 'inline-block', transformOrigin: '50% 100%' }}
              >
                {ch}
              </motion.span>
            );
          })}
        </span>
        {/* Lo spazio sta FUORI dal gruppo-parola: dentro un inline-block un
            nbsp impedirebbe anche l'andata a capo tra le parole, e "di emozioni"
            diventerebbe un blocco atomico che sfora sugli schermi stretti. */}
        {wi < parole.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </span>
  );
};

const useTelefono = () => {
  const [telefono, setTelefono] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 767px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(max-width: 767px)');
    if (!mq) return undefined;
    const cambia = () => setTelefono(mq.matches);
    mq.addEventListener?.('change', cambia);
    return () => mq.removeEventListener?.('change', cambia);
  }, []);
  return telefono;
};

// Dove sta la finestra a riposo, in % del palco [alto, destra, basso, sinistra].
// I lati coincidono col margine di pagina (--gutter vale 5vw fino a 1440px).
// Il pulsante pausa (pausaClassName qui sotto) ripete alto e destra: vanno insieme.
const FINESTRA = { desktop: [9, 5, 9, 55], telefono: [57, 5.2, 3.5, 5.2] };

/**
 * Il titolo, disegnato due volte: in inchiostro sulla carta e in carta dentro
 * la finestra. Le due copie sono sovrapposte al pixel, e la finestra ritaglia
 * la seconda: dove il video passa sopra le lettere, le lettere cambiano colore.
 * Solo la copia inchiostro e' l'<h1> e porta il link vero; la copia chiara e'
 * decorazione (aria-hidden) con un pulsante finto allineato al pixel.
 */
const Testo = ({ chiaro = false, sfuma, sale }) => {
  const { t } = useLang();
  const Titolo = chiaro ? 'p' : 'h1';
  return (
    <div
      aria-hidden={chiaro || undefined}
      className="wrap pointer-events-none relative flex h-full flex-col pt-[7svh] md:justify-center md:pb-[4vh] md:pt-0"
    >
      <motion.div style={{ y: sale }}>
        <Titolo
          aria-hidden={chiaro || undefined}
          aria-label={chiaro ? undefined : `${t('home.h1a')} ${t('home.h1b')} · VdE Tivoli 1996`}
          className="font-display text-[clamp(3.4rem,10.4vw,11rem)] font-medium leading-[0.84] tracking-[-0.035em] [font-variation-settings:'opsz'_96]"
        >
          <Lettere text={t('home.h1a')} className={`block ${chiaro ? 'text-carta' : ''}`} />
          <Lettere
            text={t('home.h1b')}
            delay={0.25}
            className={`accento block !font-normal ${chiaro ? 'text-carta' : 'text-marsala'}`}
          />
        </Titolo>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.6 }}
        style={{ opacity: sfuma }}
        className="mt-8 md:mt-12"
      >
        <p className={`prosa max-w-[40ch] text-base md:max-w-[32ch] md:text-lg lg:max-w-[40ch] ${chiaro ? '!text-carta/85' : ''}`}>
          {t('home.lead')}
        </p>
        {chiaro ? (
          // segnaposto: tiene allineata la copia chiara, il pulsante vero resta sotto
          <span className="btn invisible mt-6 md:mt-8">{t('nav.build')}</span>
        ) : (
          <Link
            to="/componi"
            data-testid="home-hero-cta"
            className="btn btn-solid pointer-events-auto mt-6 md:mt-8"
          >
            {t('nav.build')}
          </Link>
        )}
      </motion.div>
    </div>
  );
};

/**
 * L'hero: il titolo sulla carta e, accanto, una finestra sul laboratorio (il
 * filmato reale VdE, senza il cartello finale). Scorrendo, il palco resta fermo
 * e la finestra si allarga fino a tutto schermo, passando sopra il titolo.
 * Con «riduci animazioni» il palco non si ferma: finestra e poster, fermi.
 */
export const Hero = () => {
  const { t } = useLang();
  const ridotta = useReducedMotion();
  const telefono = useTelefono();
  const palco = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: palco, offset: ['start start', 'end end'] });

  const da = FINESTRA[telefono ? 'telefono' : 'desktop'];
  const apertura = useTransform(p, [0.04, 0.7], [0, 1], { clamp: true });
  const ritaglio = useTransform(apertura, (v) => {
    // ease-in-out: parte piano, accelera, si posa
    const e = v < 0.5 ? 4 * v * v * v : 1 - (-2 * v + 2) ** 3 / 2;
    return `inset(${da.map((x) => `${(x * (1 - e)).toFixed(3)}%`).join(' ')})`;
  });
  const zoom = useTransform(apertura, [0, 1], [1.07, 1]);
  const velo = useTransform(apertura, [0, 1], [0.3, 0.46]);
  const sfuma = useTransform(p, [0, 0.22], [1, 0]);
  const sale = useTransform(p, [0, 1], ['0vh', '-5vh']);
  const didascalia = useTransform(p, [0.7, 0.86], [0, 1]);
  const didascaliaY = useTransform(p, [0.7, 0.86], [18, 0]);
  const finestraFerma = `inset(${da.map((x) => `${x}%`).join(' ')})`;

  return (
    <section
      ref={palco}
      data-testid="home-hero"
      className={`relative ${ridotta ? '' : 'h-[180vh] md:h-[200vh]'}`}
    >
      <div className="sticky top-[var(--altezza-header)] h-[calc(100svh-var(--altezza-header))] min-h-[560px] overflow-hidden bg-carta">
        <Testo sfuma={ridotta ? undefined : sfuma} sale={ridotta ? undefined : sale} />

        <motion.div
          className="absolute inset-0 z-10"
          style={{ clipPath: ridotta ? finestraFerma : ritaglio }}
        >
          {/* la finestra si alza da sotto al primo caricamento, poi la guida lo scroll */}
          <motion.div
            className="absolute inset-0 bg-inchiostro"
            initial={ridotta ? false : { clipPath: 'inset(100% 0% 0% 0%)' }}
            animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            transition={{ duration: 1.3, delay: 0.35, ease: [0.76, 0, 0.24, 1] }}
            style={ridotta ? undefined : { '--zoom': zoom }}
          >
            <LoopVideo
              src="/assets/video/hero-mani.mp4"
              srcMobile="/assets/video/hero-mani-v.mp4"
              poster={telefono ? '/assets/video/hero-mani-v-poster.jpg' : '/assets/video/hero-mani-poster.jpg'}
              width={telefono ? 406 : 1280}
              height={720}
              priority
              testid="home-hero-video"
              className="absolute inset-0"
              mediaClassName="scale-[var(--zoom,1)]"
              pausaClassName="top-[calc(57%+12px)] right-[calc(5.2%+12px)] md:top-[calc(9%+16px)] md:right-[calc(5%+16px)]"
            />
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-inchiostro"
              style={{ opacity: ridotta ? 0.3 : velo }}
            />
            <div className="absolute inset-0">
              <Testo chiaro sfuma={ridotta ? undefined : sfuma} sale={ridotta ? undefined : sale} />
            </div>
            {!ridotta && (
              <motion.div
                style={{ opacity: didascalia, y: didascaliaY }}
                className="pointer-events-none absolute inset-x-0 bottom-0"
              >
                <div className="wrap pb-8 text-carta md:pb-12">
                  <p className="max-w-[30ch] pr-14 font-display text-2xl leading-tight md:pr-0">
                    {t('home.heroReelTitle')}
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
};
